import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as selectors from 'app/state/selectors/index.js';
import { operations as accountOperations } from 'app/state/ducks/accounts/index.js';

export interface UserAccountInfo {
  isLoggedIn: boolean;
  hasSubscription: boolean;
  creditsRemaining: number;
  hasUnlimitedDownloads: boolean;
  canUserPurchase: boolean;
  hasWatermarkedDraftPreviews: boolean;
  isOnPartnerSite: boolean;
  hasUsedCoupon: boolean;
  isFreeUser: boolean;
}

export const useUserAccountInfo = (): UserAccountInfo => {
  // User account info ---------------------------------------------------------
  const {
    isLoggedIn,
    accountSubscriptions,
    hasUnlimitedDownloads,
    canUserPurchase,
    accountVideoCredits,
    accountGUID,
    hasWatermarkedDraftPreviews,
    isOnPartnerSite,
    hasUsedCoupon,
  } = useSelector((state) => ({
    isLoggedIn: selectors.isLoggedIn(state),
    accountSubscriptions: selectors.getAllAccountSubscriptions(state),
    accountVideoCredits: selectors.getAccountVideoCreditCount(state),
    hasUnlimitedDownloads: selectors.hasUnlimitedDownloads(state),
    canUserPurchase: selectors.canUserPurchase(state),
    accountGUID: selectors.getAccountGUID(state),
    hasWatermarkedDraftPreviews: selectors.getHasWatermarkedDraftPreviews(state),
    isOnPartnerSite: selectors.getIsOnPartnerSite(state),
    hasUsedCoupon: selectors.hasUsedCoupon(state),
  }));

  // Fetching the user's subscriptions on init, or when the accountGUID changes
  const [shouldRefetchSubscriptions, setShouldRefetchSubscriptions] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (shouldRefetchSubscriptions) {
      dispatch(accountOperations.loadAccountSubscriptions(accountGUID));
      setShouldRefetchSubscriptions(false);
    }
  }, [accountGUID, dispatch, shouldRefetchSubscriptions]);

  // Checking if the user has any subscriptions and or credits remaining
  const hasSubscription = useMemo(() => {
    if (accountSubscriptions) {
      // ensure we filter out all undefined values from the array
      return accountSubscriptions.filter(Boolean).length > 0;
    }

    return false;
  }, [accountSubscriptions]);

  // We're considering a free user to be someone who doesn't have a subscription, doesn't have unlimited downloads,
  // isn't associated with a partner, and doesn't have an active coupon.
  const isFreeUser = useMemo(() => {
    return !hasSubscription && !hasUnlimitedDownloads && !isOnPartnerSite && !hasUsedCoupon;
  }, [hasSubscription, hasUnlimitedDownloads, isOnPartnerSite, hasUsedCoupon]);

  return {
    isLoggedIn: isLoggedIn,
    hasSubscription,
    creditsRemaining: accountVideoCredits,
    hasUnlimitedDownloads: hasUnlimitedDownloads,
    canUserPurchase: canUserPurchase,
    hasWatermarkedDraftPreviews: hasWatermarkedDraftPreviews,
    isOnPartnerSite: isOnPartnerSite,
    hasUsedCoupon: hasUsedCoupon,
    isFreeUser: isFreeUser,
  };
};
